import { useParams } from 'react-router-dom';

import { createProof } from '@cortis/proof-lifecycle';

import { Api } from '../lib/api.js';
import { useApi, useWallet } from '../lib/wallet.jsx';
import { Badge, Card, ErrorState, Hash, Skeleton } from '../components/ui.jsx';
import { ProofPanel } from '../components/ProofPanel.jsx';

/** A single run's proof, in full. Safe fields only — never the manifest. */
export function RunDetail() {
  const { runId } = useParams();
  const { sessionReady } = useWallet();
  const { data, loading, error, reload } = useApi(() => Api.proof(runId), [runId, sessionReady], { skip: !sessionReady });

  if (error) return <ErrorState error={error} onRetry={reload} context="This proof could not be loaded" />;
  if (!sessionReady || loading || !data) return <Skeleton rows={6} />;

  return (
    <div className="stack">
      <header className="page-header">
        <div>
          <h1 className="page-title">Run proof</h1>
          <p className="page-subtitle mono">{data.runId}</p>
        </div>
        <Badge tone={data.confirmed ? 'success' : 'pending'}>{data.proofBadge.label}</Badge>
      </header>

      <ProofPanel
        proof={{
          ...createProof({ runId: data.runId, runIdHash: data.runIdHash }),
          state: data.proofState,
          txHash: data.attestation?.txHash ?? null,
          confirmations: data.confirmed ? 3 : 0,
          indexed: data.confirmed,
        }}
        deliverable={null}
        onRetry={() => {}}
      />

      <Card title="Verifiable fields">
        <dl className="facts">
          <Row label="Outcome" value={data.terminalOutcome ?? '—'} />
          <Row label="Run ID hash" value={<Hash value={data.runIdHash} label="run id hash" />} />
          <Row label="Evidence root" value={<Hash value={data.evidenceRoot} label="evidence root" />} />
          {data.workflowIdHash && <Row label="Workflow hash" value={<Hash value={data.workflowIdHash} label="workflow hash" />} />}
          {data.attestation && (
            <>
              <Row label="Certificate digest" value={<Hash value={data.attestation.certificateDigest} label="digest" />} />
              <Row label="Attestor" value={<Hash value={data.attestation.attestorAddress} label="attestor" />} />
              <Row label="Contract" value={<Hash value={data.attestation.contractAddress} label="contract" />} />
              <Row label="Chain" value={<span className="mono">{data.attestation.chainId}</span>} />
              <Row label="Transaction" value={<Hash value={data.attestation.txHash} label="transaction" />} />
              <Row label="Block" value={<span className="mono">{data.attestation.blockNumber ?? '—'}</span>} />
              <Row label="Confirmed" value={data.attestation.confirmedAt ? new Date(data.attestation.confirmedAt).toLocaleString() : '—'} />
            </>
          )}
        </dl>
        <p className="text-muted">
          These are commitments, not content. The prompt, the response, the evidence manifest and any retrieved memory
          are stored encrypted and never appear on chain.
        </p>
      </Card>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="facts__row">
      <dt className="facts__label">{label}</dt>
      <dd className="facts__value">{value}</dd>
    </div>
  );
}
